import startCase from 'lodash/startCase'
import { BsBadge3D } from 'react-icons/bs'
import { FiGlobe } from 'react-icons/fi'
import { LiaRoadSolid } from 'react-icons/lia'

/**
 * The road / satellite / 3D display modes shared by the Mapbox maps. A map is keyed on
 * its style so it remounts on change, and passes `mapStyleUrl(style)` as `mapStyle`.
 */
export type MapStyle = 'road' | 'satellite' | '3d'

export const MAP_STYLES: Record<MapStyle, { pitch: number; bearing: number; icon: React.ReactNode }> = {
  road: { pitch: 0, bearing: 0, icon: <LiaRoadSolid className="w-4 h-4" /> },
  satellite: { pitch: 0, bearing: 0, icon: <FiGlobe className="w-4 h-4" /> },
  '3d': { pitch: 40, bearing: 0, icon: <BsBadge3D className="w-4 h-4" /> },
}

export const mapStyleUrl = (style: MapStyle): string => {
  if (style === '3d') return 'mapbox://styles/mapbox/standard'
  if (style === 'satellite') return 'mapbox://styles/mapbox/satellite-streets-v12'
  return 'mapbox://styles/mapbox/streets-v9'
}

interface MapStyleTogglesProps {
  style: MapStyle
  /** Called only when the style actually changes, with the camera that style uses. */
  onChange: (style: MapStyle, camera: { pitch: number; bearing: number }) => void
  /** Positioning classes for the floating button group. */
  className?: string
}

export const MapStyleToggles = ({ style, onChange, className = 'top-4 right-4' }: MapStyleTogglesProps) => (
  <div className={`absolute ${className} z-10 bg-base-100 rounded-lg shadow-lg p-1 flex flex-col gap-1`}>
    {(Object.entries(MAP_STYLES) as [MapStyle, (typeof MAP_STYLES)[MapStyle]][]).map(([key, styleProps]) => (
      <button
        key={key}
        type="button"
        onClick={() => {
          if (key === style) return
          onChange(key, { pitch: styleProps.pitch, bearing: styleProps.bearing })
        }}
        className={`tooltip tooltip-left tooltip-primary btn btn-sm btn-square ${style === key ? 'btn-primary' : 'btn-ghost'}`}
        data-tip={key === '3d' ? '3D' : startCase(key)}
      >
        {styleProps.icon}
      </button>
    ))}
  </div>
)
